import { Component } from '@angular/core';
import { Warehouse } from '../../domain/warehouse.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { Pallet, PalletSearchParams } from '../../domain/inventory.model';
import { AccountService } from '../../services/account.service';
import { PalletService } from '../../services/pallet.service';
import { Account, PageableAccount } from '../../domain/account.model';
import { AuthService } from '../../services/auth.service';
import { AccountDropdown } from '../../domain/accountDropdown.model';
import { ColumnConfig2 } from '../../domain/column_config.model';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
    selector: 'app-pallets-inventory',
    templateUrl: './pallets-inventory.component.html',
    styleUrl: './pallets-inventory.component.scss',
    providers: [MessageService, ConfirmationService],
    standalone: false
})

export class PalletsInventoryComponent {
  pallets: Pallet[] = [];
  accounts: any[] = [];
  userAccount: Account = {};
  totalRecords: number = 0;
  loading: boolean = true;
  accountLoading: boolean = false;
  filters: any = {};
  statuses = [
    { label: 'COMPLETADA', value: 'COMPLETADA' },
    { label: 'CREADA', value: 'CREADA' },
    { label: 'ELIMINADA', value: 'ELIMINADA' },
  ];
  floorings = [
    { label: 'Piso', value: 'Piso' },
    { label: 'Rack', value: 'Rack' },
  ];
  warehouses: Warehouse[] = [
    {
      id: 1,
      name: 'Bodega Zapopan'
    }
  ];

  //Account dropdown
  lastQuery: string = '';
  searchAccountSubject = new Subject<string>();
  searchAccountQuery: string = '';

  //Search form
  searchForm: FormGroup = this.fb.group({
    warehouse: [''],
    accountId: [null],
    palletCode: [null],
    status: [null],
    flooring: [null],
    location: [null],
    creationDateFrom: [null],
    creationDateTo: [null],
  });
  tableRows: number = 30;

  columnsToggle: ColumnConfig2[] = [
    { field: 'palletId', header: '#', alwaysVisible: true, visible: true },
    { field: 'qrCode', header: 'Código de Palet', visible: true, sortable: true },
    { field: 'location', header: 'Ubicación', visible: false },
    { field: 'status', header: 'Estado', visible: true },
    { field: 'depth', header: 'Largo', visible: true },
    { field: 'width', header: 'Ancho', visible: true },
    { field: 'height', header: 'Alto', visible: true },
    { field: 'creationDate', header: 'Creada', visible: true, sortable: true, isDate: true },
    { field: 'updateDate', header: 'Actualizada', visible: true, sortable: true, isDate: true },
    { field: 'releaseDate', header: 'Salida', visible: true, sortable: true, isDate: true, onlyIfCompleted: true }
  ];

  selectedColumnsPalets: ColumnConfig2[] = [...this.columnsToggle];
  appliedColumnsPalets: ColumnConfig2[] = [...this.columnsToggle.filter(c => c.visible || c.alwaysVisible)];
  selectAllPalets = true;
  searchTerm: string = '';

  role: string = '';
  allowedFields: string[] = [];
  user: any = {};
  userKey: string = '';
  storageKey: string = '';
  savedColumnsRaw: string | null = null;

  private allowedColumnsByRolePallets: { [key: string]: string[] } = {
    BILD_ADMIN: ['palletId', 'qrCode', 'location', 'status', 'depth', 'width', 'height', 'creationDate', 'updateDate', 'releaseDate'],
    BILD_OPERADOR: ['palletId', 'qrCode', 'status', 'depth', 'width', 'height', 'creationDate', 'releaseDate'],
    BILD_ENCARGADO: ['palletId', 'qrCode', 'status', 'depth', 'width', 'height', 'creationDate', 'releaseDate'],
    BILD_VENTAS: ['palletId', 'qrCode', 'status', 'creationDate', 'releaseDate'],
    CLIENTE_ADMIN: ['palletId', 'qrCode', 'status', 'depth', 'width', 'height', 'creationDate', 'updateDate', 'releaseDate'],
    CLIENTE_OPERADOR: ['palletId', 'qrCode', 'status', 'depth', 'width', 'height', 'creationDate', 'updateDate', 'releaseDate'],
    DEFAULT_ROLE: ['palletId', 'qrCode', 'status', 'creationDate', 'releaseDate']
  }

  constructor(private palletService: PalletService, private fb: FormBuilder, private accountService: AccountService, private authService: AuthService,
    private confirmationService: ConfirmationService, private messageService: MessageService) {
    this.role = this.authService.getUserRole() || 'DEFAULT_ROLE';
    this.allowedFields = this.allowedColumnsByRolePallets[this.role] || [];

    this.user = this.authService.getUserDetails();
    this.userKey = this.user?.userId;
    this.storageKey = `savedColumnsPallets_${this.userKey}`;
    this.savedColumnsRaw = localStorage.getItem(this.storageKey);
    if (this.savedColumnsRaw) {
      this.appliedColumnsPalets = JSON.parse(this.savedColumnsRaw);
      this.columnsToggle.forEach(col => col.visible = this.appliedColumnsPalets.some(sc => sc.field === col.field));
      this.selectAllPalets = this.appliedColumnsPalets.length === this.columnsToggle.length;
    } else {
      this.validateCorrespondingColumnsForUserPallets();
      this.adjustDefaultColumnsForClientPallets();
    }
  }

  ngOnInit(): void {
    this.searchForm.patchValue({ warehouse: this.warehouses[0].name });
    this.fetchAccounts();
    this.searchAccountSubject.pipe(debounceTime(800)).subscribe(searchText => {
      this.searchAccountQuery = searchText;
      this.getAccounts();
    });
  }

  get filteredColumns() {
    const allowedColumns = this.toggleableColumnsPalets.filter(col => this.allowedFields.includes(col.field));

    if (!this.searchTerm) {
      return allowedColumns;
    }
    return allowedColumns.filter(col =>
      col.header.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  get toggleableColumnsPalets(): ColumnConfig2[] {
    return this.columnsToggle.filter(c => !c.alwaysVisible);
  }

  toggleAllPalets() {
    this.columnsToggle.forEach(c => {
      if (!c.alwaysVisible && this.allowedFields.includes(c.field)) c.visible = this.selectAllPalets;
    });
    this.selectedColumnsPalets = this.columnsToggle.filter(c => c.visible || c.alwaysVisible);
  }

  clearSelectionPalets() {
    this.columnsToggle.forEach(c => {
      if (!c.alwaysVisible && this.allowedFields.includes(c.field)) c.visible = false;
    });
    this.selectedColumnsPalets = this.columnsToggle.filter(c => c.alwaysVisible);
    this.selectAllPalets = false;
  }

  saveSelectionPalets() {
    this.appliedColumnsPalets = this.columnsToggle.filter(c => c.visible || c.alwaysVisible);
    localStorage.setItem(this.storageKey, JSON.stringify(this.appliedColumnsPalets));
  }

  resolveField(item: any, field: string): any {
    if (!item || !field) return '';
    return field.split('.').reduce((obj, key) => obj && obj[key], item);
  }

  // Access control methods
  get hasAccountAccess(): boolean {
    return this.authService.canAccessAction('articlesInventory', 'accountSelection');
  }

  get hasEditAccess(): boolean {
    return this.authService.canAccessAction('articlesInventory', 'edit');
  }

  get hasDeleteAccess(): boolean {
    return this.authService.canAccessAction('palletsInventory', 'delete');
  }

  fetchAccounts() {
    if (this.hasAccountAccess) {
      this.getAccounts();
    } else {
      this.getAccount();
    }
  }

  getAccounts() {
    this.accountLoading = true;
    this.accountService.searchAllAccounts(this.searchAccountQuery).subscribe((response: PageableAccount) => {
      this.accounts = response.content;
      this.accountLoading = false;
    });
  }

  getAccount() {
    this.searchForm.get('accountId')?.disable();
    this.accountLoading = true;
    this.userAccount = this.authService.getUserAccount();
    this.searchForm.patchValue({ accountId: this.userAccount.accountId });
    this.accountService.getDropdownAccount(this.userAccount.accountId).subscribe((response: AccountDropdown[]) => {
      this.accounts = response;
      this.accountLoading = false;
    });
  }

  search() {
    this.loadItems({ sortField: 'palletId', first: 0, rows: this.tableRows });
  }

  loadAccounts(event: any) {
    this.searchAccountSubject.next(event.filter || '');
  }

  resetForm() {
    this.searchForm.reset();
    this.searchForm.patchValue({ warehouse: this.warehouses[0].name });
    this.searchForm.patchValue({ accountId: this.userAccount.accountId });
    this.search();
    //this.items = [];
  }

  loadItems(event: any) {
    this.loading = true;
    const sortField = event.sortField;
    const sortOrder = event.sortOrder === 1 ? 'asc' : 'desc';
    const params: PalletSearchParams = {
      ...this.searchForm.value,
      creationDateFrom: this.formatDate(this.searchForm.value.creationDateFrom),
      creationDateTo: this.formatDate(this.searchForm.value.creationDateTo),
      page: event.first / event.rows,
      size: event.rows,
      sort: `${sortField}`,
      sortDirection: `${sortOrder}`,
      warehouse: this.searchForm.get('warehouse')?.value,
      accountId: this.searchForm.get('accountId')?.value
    };

    this.palletService.searchItems(params).subscribe((data) => {
      this.pallets = data.content;
      this.totalRecords = data.totalElements;
      this.loading = false;
    });
  }

  private formatDate(date: Date | null): string | null {
    return date ? date.toISOString() : null;
  }

  deletePallet(pallet: Pallet) {
    this.confirmationService.confirm({
      message: '¿Estás seguro que quieres eliminar el palet ' + pallet.qrCode + '?',
      header: 'Confirmación',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.palletService.deletePallet(pallet.palletId!).subscribe({
          next: () => {
            pallet.status = 'ELIMINADA';
            this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Palet eliminado', life: 5000 });
          },
          error: (error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo eliminar el palet' });
          }
        });
      }
    });
  }

  getSeverity(status: string) {
    switch (status) {
      case 'COMPLETADA':
        return 'success';
      case 'CREADA':
        return 'info';
      case 'ELIMINADA':
        return 'danger';
      default:
        return undefined;
    }
  }

  validateCorrespondingColumnsForUserPallets() {
    this.columnsToggle.forEach(col => {
      col.visible = col.alwaysVisible || this.allowedFields.includes(col.field);
    });
  }

  adjustDefaultColumnsForClientPallets(): void {
    if (!this.authService.isClientUser()) {
      this.appliedColumnsPalets = this.columnsToggle.filter(c => c.visible || c.alwaysVisible);
      return;
    }
    for (const col of this.columnsToggle) {
      switch (col.field) {
        case 'depth':
        case 'width':
        case 'height':
          col.visible = false;
          break;
        case 'updateDate':
          col.visible = true;
          break;
        default:
          break;
      }
    }
    this.appliedColumnsPalets = this.columnsToggle.filter(c => c.visible || c.alwaysVisible);
  }
}
